import React, { createContext, useContext, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { getDriverPortalInstanceService } from '../services/driverPortalInstanceService';
import { submitInitialEmailService } from '../services/authServices';
import { removeAuthInfoFromLocalStorage } from '../utils/helper';
import { isUniversalLoginHost, getCustomerDetails } from '../config';

const DriverPortalInstancesContext = createContext();

// Stop bouncing after this many redirect attempts in one page session.
const MAX_UNIVERSAL_REDIRECTS = 2;

export function DriverPortalInstancesProvider({ children }) {
    const [instanceData, setInstanceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [redirecting, setRedirecting] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const hostname = window.location.hostname;

        // Clear localStorage AND the native AsyncStorage backup (via authLogout), else the mobile
        // OS-restore path re-injects the token and loops.
        const fullLogoutToLogin = () => {
            removeAuthInfoFromLocalStorage();
            window.postToRN?.({ type: 'authLogout' });
            window.location.href = '/login';
        };

        // Authenticated on the universal host (resolves no scope): send the driver to their company
        // domain, where scope derives from the hostname.
        const attemptUniversalRedirect = async () => {
            const attempts = Number(sessionStorage.getItem('dp_universalRedirectCount') || 0);
            const email = getCustomerDetails()?.email;

            if (!email || attempts >= MAX_UNIVERSAL_REDIRECTS) {
                fullLogoutToLogin();
                return;
            }

            try {
                // Auto-includes stored scope: filters to one portal when set, else returns all.
                const res = await submitInitialEmailService(email);
                const usable = (res?.data?.portals || []).filter(
                    (p) => p.hostname && !isUniversalLoginHost(p.hostname) && p.hostname !== hostname
                );

                if (usable.length === 1) {
                    const portal = usable[0];
                    sessionStorage.setItem('dp_universalRedirectCount', String(attempts + 1));
                    const token = JSON.parse(localStorage.getItem('token'));
                    // localStorage only — keep AsyncStorage so "stay logged in" survives, but don't
                    // leave a token on this origin that could outlive a subdomain logout.
                    removeAuthInfoFromLocalStorage();
                    const url = new URL(`${window.location.protocol}//${portal.hostname}/sso`);
                    url.searchParams.append('token', token);
                    url.searchParams.append('modelTypeID', portal.modelTypeID);
                    url.searchParams.append('entityID', portal.entityID);
                    window.location.href = url.toString();
                    return;
                }

                // None resolvable, or ambiguous with no stored scope: let /login handle selection.
                fullLogoutToLogin();
            } catch (err) {
                console.error('Error resolving driver portal for universal-host redirect:', err);
                fullLogoutToLogin();
            }
        };

        const fetchInstance = async () => {
            const hostname = window.location.hostname;
            if (isUniversalLoginHost(hostname)) {
                if (localStorage.getItem('token')) {
                    setRedirecting(true);
                    attemptUniversalRedirect();
                    return;
                }
                // Pre-login: clear stale scope so login enumerates all portals; reset the guard.
                localStorage.removeItem('dp_modelTypeID');
                localStorage.removeItem('dp_entityID');
                sessionStorage.removeItem('dp_universalRedirectCount');
                setInstanceData({ universal: true });
                setLoading(false);
                return;
            }

            try {
                const data = await getDriverPortalInstanceService(hostname);
                localStorage.setItem('dp_modelTypeID', String(data.modelTypeID));
                localStorage.setItem('dp_entityID', String(data.entityID));
                setInstanceData(data);
            } catch (err) {
                console.error('Error fetching driver portal instance:', err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInstance();
    }, []);

    if (redirecting) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }
    if (loading) return null;
    if (error) return <div>Error loading application configuration.</div>;

    return (
        <DriverPortalInstancesContext.Provider
            value={{ modelTypeID: instanceData?.modelTypeID, entityID: instanceData?.entityID }}
        >
            {children}
        </DriverPortalInstancesContext.Provider>
    );
}

export function useDriverPortalInstance() {
    const context = useContext(DriverPortalInstancesContext);
    if (!context) {
        throw new Error('useDriverPortalInstance must be used within a DriverPortalInstancesProvider');
    }
    return context;
}
